'use client';

import {
  Turnstile,
  TurnstileInstance,
  TurnstileProps,
} from '@marsidev/react-turnstile';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

interface BaseCaptchaFieldProps {
  siteKey: string | undefined;
  options?: TurnstileProps;
  nonce?: string;
}

interface StandaloneCaptchaFieldProps extends BaseCaptchaFieldProps {
  onTokenChange: (token: string) => void;
  onInstanceChange?: (instance: TurnstileInstance | null) => void;
  control?: never;
  name?: never;
}

interface ReactHookFormCaptchaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends BaseCaptchaFieldProps {
  control: Control<TFieldValues>;
  name: TName;
  onTokenChange?: never;
  onInstanceChange?: never;
}

interface TurnstileCaptchaProps extends BaseCaptchaFieldProps {
  onTokenChange: (token: string) => void;
  onInstanceChange?: (instance: TurnstileInstance | null) => void;
}

type CaptchaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> =
  | StandaloneCaptchaFieldProps
  | ReactHookFormCaptchaFieldProps<TFieldValues, TName>;

/**
 * @name CaptchaField
 * @description Self-contained captcha component with two modes:
 *
 * **Standalone mode** - For use outside react-hook-form:
 * ```tsx
 * <CaptchaField
 *   siteKey={siteKey}
 *   onTokenChange={setToken}
 * />
 * ```
 *
 * **React Hook Form mode** - Automatic form integration:
 * ```tsx
 * <CaptchaField
 *   siteKey={siteKey}
 *   control={form.control}
 *   name="captchaToken"
 * />
 * ```
 */
export function CaptchaField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CaptchaFieldProps<TFieldValues, TName>) {
  if (props.control) {
    return <ControlledCaptchaField {...props} />;
  }

  return <TurnstileCaptcha {...props} />;
}

function ControlledCaptchaField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  ...props
}: ReactHookFormCaptchaFieldProps<TFieldValues, TName>) {
  const { field } = useController({ control, name });

  return <TurnstileCaptcha {...props} onTokenChange={field.onChange} />;
}

function TurnstileCaptcha({
  siteKey,
  options,
  nonce,
  onTokenChange,
  onInstanceChange,
}: TurnstileCaptchaProps) {
  if (!siteKey) {
    return null;
  }

  const defaultOptions: Partial<TurnstileProps> = {
    options: {
      size: 'invisible',
    },
  };

  return (
    <Turnstile
      ref={(instance) => {
        if (instance) {
          onInstanceChange?.(instance);
        }
      }}
      siteKey={siteKey}
      onSuccess={onTokenChange}
      scriptOptions={{
        nonce,
      }}
      {...defaultOptions}
      {...options}
    />
  );
}
